import KPIChangeDisplay from '@components/Display/KPIChangeDisplay/KPIChangeDisplay';
import { Divider, Stack } from '@esgian/esgianui';
import { findSignal, getGeneratorSignalsMap } from '@api/helpers';
import { getPercentDiff, getValue } from '@helpers';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

function PowerMgmtComparisonKpis({ mainData, baselineData, loading }) {
  const getAvgData = (data, key) => {
    const dataSignal = findSignal(data.signals, key)?.timeSeriesValues ?? {};
    let dataArray = Object.values(dataSignal)?.filter(
      (value) => value !== null && value !== 'null'
    );
    let sum = dataArray?.reduce((a, b) => a + getValue(b), 0);
    return { sum: sum, count: dataArray.length };
  };

  const sumObjects = (current, res) => {
    return { sum: current.sum + res.sum, count: current.count + res.count };
  };

  // Fold one engine's period summary into the running fleet total. Engines without a
  // summary are skipped so they don't drag the average towards zero.
  const addEngineSummary = (current, summaryValue) => {
    if (summaryValue === null || summaryValue === undefined) return current;
    return sumObjects(current, { sum: getValue(summaryValue), count: 1 });
  };

  const kpis = useMemo(() => {
    if (!mainData || !mainData.signals || !baselineData || !baselineData.signals) return {};
    let mainSfoc = { sum: 0, count: 0 };
    let baseSfoc = { sum: 0, count: 0 };
    let mainLavg = { sum: 0, count: 0 };
    let baseLavg = { sum: 0, count: 0 };

    let mainEngOn = getAvgData(mainData, 'EngOn');
    let baseEngOn = getAvgData(baselineData, 'EngOn');
    mainEngOn = mainEngOn.count ? mainEngOn.sum / mainEngOn.count : null;
    baseEngOn = baseEngOn.count ? baseEngOn.sum / baseEngOn.count : null;

    const mainGeneratorMap = getGeneratorSignalsMap(mainData.signals);
    const baselineGeneratorMap = getGeneratorSignalsMap(baselineData.signals);
    // Compare the engines the main rig actually reports - generator numbers are not
    // guaranteed to be a contiguous 1..N range.
    Object.keys(mainGeneratorMap).forEach((engineNum) => {
      mainSfoc = addEngineSummary(mainSfoc, mainGeneratorMap[engineNum]?.['SFOC_Summary']);
      baseSfoc = addEngineSummary(baseSfoc, baselineGeneratorMap[engineNum]?.['SFOC_Summary']);
      mainLavg = addEngineSummary(mainLavg, mainGeneratorMap[engineNum]?.['Load_Summary']);
      baseLavg = addEngineSummary(baseLavg, baselineGeneratorMap[engineNum]?.['Load_Summary']);
    });

    mainSfoc = mainSfoc.count ? mainSfoc.sum / mainSfoc.count : null;
    baseSfoc = baseSfoc.count ? baseSfoc.sum / baseSfoc.count : null;
    mainLavg = mainLavg.count ? mainLavg.sum / mainLavg.count : null;
    baseLavg = baseLavg.count ? baseLavg.sum / baseLavg.count : null;

    const safeDiff = (a, b) => (a != null && b != null) ? a - b : null;
    const safePercent = (base, main) => (base != null && main != null) ? getPercentDiff(base, main) : null;

    return {
      engOn: {
        value: safeDiff(mainEngOn, baseEngOn),
        percent: safePercent(baseEngOn, mainEngOn)
      },
      sfoc: {
        value: safeDiff(mainSfoc, baseSfoc),
        percent: safePercent(baseSfoc, mainSfoc)
      },
      load: {
        value: safeDiff(mainLavg, baseLavg),
        percent: safePercent(baseLavg, mainLavg)
      }
    };
  }, [mainData, baselineData]);

  return (
    <Stack
      direction={'row'}
      justifyContent={'space-evenly'}
      divider={<Divider sx={{ height: 'inherit' }} orientation={'vertical'} />}>
      <KPIChangeDisplay
        loading={loading}
        percentChange={kpis.sfoc?.percent}
        value={kpis.sfoc?.value ?? null}
        unit="g/kWh"
        title={`Engines avg SFOC`}
        tooltipEnterDelayMs={500}
        tooltipText={'This figure indicates the average SFOC difference between the selected rig and the baseline. Lower SFOC means that the engines have been running on a higher and more ideal load to consume less fuel for each kWh produced.'}
      />

      <KPIChangeDisplay
        invert
        loading={loading}
        percentChange={kpis.load?.percent}
        value={kpis.load?.value ?? null}
        unit="%"
        title={`Engines avg load`}
        tooltipEnterDelayMs={500}
        tooltipText={'This figure indicates the average load difference between the selected rig and the baseline. Higher average load means that the engines have been running on a higher and more ideal load to consume less fuel for each kWh produced.'}
      />

      <KPIChangeDisplay
        loading={loading}
        percentChange={kpis.engOn?.percent}
        value={kpis.engOn?.value ?? null}
        title={`Avg engines online`}
        tooltipEnterDelayMs={500}
        tooltipText={'This figure indicates the average engines online difference between the selected rig and the baseline.'}
      />
    </Stack>
  );
}

PowerMgmtComparisonKpis.propTypes = {
  mainData: PropTypes.object,
  baselineData: PropTypes.object,
  loading: PropTypes.bool
};

PowerMgmtComparisonKpis.defaultProps = { mainData: {}, baselineData: {}, loading: false };

export default PowerMgmtComparisonKpis;
